<template>

  <SubHeading :home="home" :tabs="tabs" :currTab="selectedEncounter ? null : currTab" color="red lighten-3" @active="handleActive" />

  <!-- breadcrumbs -->
  <nav class="sba-collection-breadcrumb auth-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <a href="#" class="breadcrumb sba-collection-crumb teal-text pointable auth-home-crumb"
          @click.prevent="handleInlineClose" v-if="selectedEncounter">
          <span>Authorization Home</span>
        </a>
        <span class="breadcrumb sba-collection-crumb blue-grey-text" v-else>
          <span>Authorization Home</span>
        </span>
        <template v-if="selectedEncounter">
          <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
          <span class="breadcrumb sba-collection-crumb blue-grey-text">
            <span>View</span>
          </span>
        </template>
        <span class="sba-collection-context">Pre-Authorization Workspace</span>
      </div>
    </div>
  </nav>

  <!-- Top -->
  <div id="top"></div>

  <div class="container auth-page-shell" :class="{ 'has-detail': selectedEncounter }">
    <div class="auth-workbench sba-slide-in" :key="selectedEncounter ? `detail-${selectedEncounter.id}` : 'list'">
      <div class="auth-review-head" :class="{ 'has-inline-detail': selectedEncounter }" v-if="!selectedEncounter">
        <div class="auth-title-row">
          <div class="auth-title-block">
            <label class="active teal-text">Authorization Desk</label>
            <h5>Pre-Authorization Activity</h5>
            <p>Search, review, and open authorization requests that need clinical or operational attention.</p>
          </div>
          <button type="button" class="auth-search-toggle" @click="toggleSearchFilters" v-if="!selectedEncounter">
            <span>{{ showSearchFilters ? "Hide Search" : "Show Search" }}</span>
            <i class="material-icons">{{ showSearchFilters ? "expand_less" : "tune" }}</i>
          </button>
        </div>
      </div>

      <div class="auth-detail-return-strip" v-if="selectedEncounter">
        <button type="button" class="auth-detail-return-button" title="Back to main list" @click.prevent="handleInlineClose">
          <i class="material-icons">arrow_back</i>
        </button>
        <div>
          <strong>Request detail open</strong>
          <span>Go back to list.</span>
        </div>
      </div>

      <AuthInlineReview
        ref="authInlineReview"
        :key="`${selectedEncounter?.id || 'none'}-${initialDetailState || 'review'}`"
        :encounter-id="selectedEncounter?.id"
        :just-created="isPostAddDetail"
        :initial-state="initialDetailState"
        @close="handleInlineClose"
        @saved="handleInlineSaved"
        v-if="selectedEncounter"
      />

      <template v-else>
        <div class="auth-filter-shell" v-show="showSearchFilters">
          <div class="auth-filter-card">
            <FilterAuth :key="filterResetKey" @search="handleSearch" />
          </div>
        </div>

        <div class="auth-search-summary">
          <span class="summary-label">Current view</span>
          <div class="summary-content" v-if="searchTitle" v-html="searchTitle"></div>
          <div class="summary-content" v-else>
            <span class="summary-chip">All authorization activity</span>
          </div>
        </div>

        <!-- Vetting Marker -->
        <div id="review"></div>

        <!-- Auths -->
        <LoadingDiv v-if="!isReady" />

        <div v-else>
          <div class="progress auth-line-loader" v-if="isLoading">
            <div class="indeterminate"></div>
          </div>

          <div class="auth-list-card" v-if="!isEmpty">
            <div class="auth-row-card" :class="{ 'needs-review': hasPendingAuth(encounter) }"
              v-for="(encounter, index) in encounters" :key="encounter.id">
              <div class="auth-row-grid">
                <div class="hide-on-small-only">
                  <span class="auth-number">{{ numberBadge(index) }}</span>
                </div>

                <div class="auth-person">
                  <h6>{{ encounter.name.toUpperCase() }}</h6>
                  <div class="auth-meta-line">
                    <span class="auth-type-chip is-principal" v-if="encounter.type == 'p'">Principal</span>
                    <span class="auth-type-chip" v-if="encounter.type == 's'">Spouse</span>
                    <span class="auth-type-chip" v-if="encounter.type == 'd'">Dependant</span>
                    <span class="auth-soft-chip">{{ showAgeSex(encounter?.dob, encounter?.sex)?.toUpperCase() }}</span>
                  </div>
                  <div class="auth-contact-line">
                    <span>{{ encounter.phone || "No phone recorded" }}</span>
                    <span class="contact-dot"></span>
                    <strong>{{ encounter.enrollee_number }}</strong>
                  </div>
                  <p class="auth-client" v-if="encounter.client_name">{{ encounter.client_name }}</p>
                </div>

                <div class="auth-data-cell">
                  <span>Provider</span>
                  <p>{{ encounter.provider?.name }}</p>
                </div>

                <div class="auth-data-cell">
                  <span>Diagnosis</span>
                  <p v-if="encounter.diagnosis">{{ encounter.diagnosis }}</p>
                  <span class="auth-soft-chip is-warning" v-else>No Diagnosis</span>
                </div>

                <div class="auth-data-cell">
                  <span>{{ hasPendingAuth(encounter) ? "Waiting" : "Request" }}</span>
                  <span class="auth-status-chip is-pending" v-if="hasPendingAuth(encounter)">
                    + {{ timeSince(encounter.claims) }}
                  </span>
                  <span class="auth-status-chip" v-else>
                    {{
                      new Date(encounter.rank).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                      })
                    }}
                  </span>
                </div>

                <div class="auth-action-cell">
                  <button type="button" class="auth-message-action"
                    :class="{ 'has-unread': encounterUnreadMessageCount(encounter) }"
                    @click="handleMessages(encounter)"
                    :title="encounterMessageLabel(encounter)"
                    :aria-label="`${encounterMessageLabel(encounter)}. Open the Messages tab.`">
                    <i class="material-icons">forum</i>
                    <span>{{ encounterMessageCount(encounter) }}</span>
                    <small>Messages</small>
                    <b class="auth-message-unread" v-if="encounterUnreadMessageCount(encounter)">
                      {{ encounterUnreadMessageCount(encounter) > 99 ? "99+" : encounterUnreadMessageCount(encounter) }}
                    </b>
                  </button>
                  <button type="button" class="auth-row-action is-review" v-if="hasPendingAuth(encounter)"
                    @click="handleEdit(encounter)" title="review">
                    <i class="material-icons">edit</i>
                  </button>
                  <button type="button" class="auth-row-action" v-else @click="handleEdit(encounter)" title="details">
                    <i class="material-icons">chevron_right</i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="auth-empty-state" v-if="!encounters.length">
            <h6><img src="@/assets/img/no-user.png" width="192" class="circle responsive-img" /></h6>
            <p>No authorization record matched the current selection.</p>
          </div>

          <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItems from "@/composables/getItems";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import FilterAuth from "@/components/auth/FilterAuthComponent.vue";
import AuthInlineReview from "@/components/auth/AuthInlineReviewComponent.vue";
import { ageSex } from "@/composables/fxLibrary/coreFx.js";
import { getCache, addCache } from "@/composables/cache/cache";
import { siloMode } from '@/laravel/config';
import {
  dismissPersistentAuthorizationNotice,
  registerAuthorizationRealtimeTarget,
} from "@/composables/authorizationRealtimeCoordinator";
import { unreadEncounterMessageCount } from "@/composables/encounterMessageReadState";

export default {
  name: "authHome",
  components: { SubHeading, LoadingDiv, PaginationDiv, FilterAuth, AuthInlineReview },
  setup() {
    const response = ref(null);
    const searchTitle = ref(null);
    const hasActiveSearch = ref(false);
    const isReady = ref(false);
    const isLoading = ref(true);
    const isEmpty = ref(true);
    const error = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const getResource = ref(null);
    const showSearchFilters = ref(false);
    const selectedEncounter = ref(null);
    const authInlineReview = ref(null);
    const pendingRealtimeListPage = ref(null);
    const filterResetKey = ref(0);
    // const homeUrl = ref(null);

    // router
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { routeToTab } = useModuleNavigation();
    const isPostAddDetail = computed(() => route.query.created === "1" || route.query.created === 1);
    const initialDetailState = computed(() => String(route.query.tab || ""));
    const authListScope = () => store.state?.user?.auth_request_scope === "all" ? "all" : "active";
    const authListPerPage = (scope) => scope === "all" ? 100 : 25;
    const authSchemeId = (scheme) => Number(scheme?.id ?? scheme);
    const isValidAuthScheme = (scheme) => Number.isInteger(authSchemeId(scheme)) && authSchemeId(scheme) > 0;
    const defaultAuthSchemeId = () => {
      const schemes = (store.state?.user?.schemes || []).filter(isValidAuthScheme);
      const currentScheme = schemes.find(x => x.curr);

      return authSchemeId(currentScheme || schemes[0] || 0);
    };
    const authListUrl = (schemeId) => {
      const scope = authListScope();
      const perPage = authListPerPage(scope);
      const schemeQuery = siloMode ? `&s=${schemeId || 0}` : `&scheme_id=${schemeId || 0}`;

      return siloMode
        ? `encounter/auth/search?scope=${scope}&per_page=${perPage}${schemeQuery}`
        : `integration/oneprovider/auth?scope=${scope}&per_page=${perPage}${schemeQuery}`;
    };

    // start
    onMounted(() => {
      // page name
      document.title = "All Authorizations";
    });

    // sort silo
    onMounted(() => {
      // sort
      getResource.value = getItems;
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Pre-Auth", title: "All", icon: null, url: "auth", hash: null, });
      tabs.value.push({ id: 2, name: "Pre-Auth", title: "Add Auth", icon: "add_circle", url: "authAdd", hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.url === "auth");
      // set home
      home.value = tabs.value.find(x => x.url === "auth");
    });

    const resetRequestHome = () => {
      selectedEncounter.value = null;
      showSearchFilters.value = false;
      searchTitle.value = null;
      hasActiveSearch.value = false;
      currUrl.value = null;
      currHook.value = null;
      response.value = null;
      isReady.value = false;
      isLoading.value = true;
      isEmpty.value = true;
      filterResetKey.value += 1;
      document.title = "All Authorizations";
    };

    // handle curr status
    const handleActive = (tab) => {
      if (tab.url === "auth") {
        routeToTab(tab, { replace: true, beforeNavigate: resetRequestHome });
        return;
      }

      routeToTab(tab);
    };

    const toggleSearchFilters = () => {
      dismissPersistentAuthorizationNotice(store);
      showSearchFilters.value = !showSearchFilters.value;
    };

    const scrollToPageTop = () => {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
    };

    const hasPendingAuth = (encounter) => {
      return encounter?.has_pending_auth || encounter?.claims?.some(x => x.authorization === 'z' && x.status === 'a');
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // ageSex
    const showAgeSex = (dob, sex) => {
      const { fx } = ageSex(dob, sex);
      return fx.value;
    };

    // get time
    const timeSince = (val) => {
      // get first unresponded
      const useFirstSentDateTime = val.find(x => x.authorization == "z" && x.status == "a");

      // function
      const past = new Date(useFirstSentDateTime?.created_at);
      if (!useFirstSentDateTime?.created_at || Number.isNaN(past.getTime())) {
        return "Pending";
      }

      const now = new Date();
      const diffMs = now - past;

      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''}`;

      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `${diffHrs} hour${diffHrs !== 1 ? 's' : ''}`;

      const diffDays = Math.floor(diffHrs / 24);
      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }

    // get function
    const getItemsFromApi = (table, options = {}) => {
      const background = options.background === true;

      // get item from store (cache)
      let cache = background ? null : getCache(table);

      if (cache?.length) {
        response.value = cache;
      }

      return (async () => {
        const { res, err } = await getResource.value(table);

        if (res.value) {
          response.value = res.value.data;
          // add cache
          addCache(table, response.value);
          // set isEmpty to false
          isEmpty.value = response.value.data.length ? false : true;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    // handle search (emitted from component)
    const handleSearch = (val) => {
      dismissPersistentAuthorizationNotice(store);
      // start loading
      isLoading.value = true;
      // set current Url
      currUrl.value = val.currUrl;
      // set current Hook
      currHook.value = val.currHook;
      // set search title
      searchTitle.value = val.searchTitle;
      hasActiveSearch.value = Boolean(val.hasActiveSearch);
      // get items from function
      getItemsFromApi(currUrl.value);
    };

    // switch page
    const handlePage = (page) => {
      dismissPersistentAuthorizationNotice(store);
      // start laoding
      isLoading.value = true;
      // go back up
      scrollToPageTop();
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
    };

    // handle Edit
    const handleEdit = (encounter) => {
      dismissPersistentAuthorizationNotice(store);
      selectedEncounter.value = encounter;
      router.replace({ name: "auth", query: { encounterId: encounter.id } });
      scrollToPageTop();
    };

    const encounterMessageCount = (encounter) => {
      if (Number.isFinite(Number(encounter?.message_count))) {
        return Math.max(0, Number(encounter.message_count));
      }

      const messages = encounter?.stores?.encounter_messages;
      return Array.isArray(messages) ? messages.length : 0;
    };

    const encounterMessageLabel = (encounter) => {
      const count = encounterMessageCount(encounter);
      const unread = encounterUnreadMessageCount(encounter);
      const unreadLabel = unread ? `, ${unread} new` : "";
      return `${count} encounter message${count === 1 ? "" : "s"}${unreadLabel}`;
    };

    const encounterUnreadMessageCount = (encounter) => {
      return unreadEncounterMessageCount(store.state.user, encounter?.id);
    };

    const handleMessages = async (encounter) => {
      if (!encounter?.id) return;

      dismissPersistentAuthorizationNotice(store);
      await router.replace({
        name: "auth",
        query: { encounterId: encounter.id, tab: "messages" },
      });
      scrollToPageTop();
    };

    const syncSchemeFromDetail = (payload) => {
      const detailSchemeId = payload?.scheme?.id || payload?.scheme_id;
      const userSchemes = store.state?.user?.schemes;

      if (!detailSchemeId || !userSchemes?.length) {
        return false;
      }

      const currentScheme = userSchemes.find(x => x.curr);

      if (String(currentScheme?.id) === String(detailSchemeId)) {
        return false;
      }

      const nextScheme = userSchemes.find(x => String(x.id) === String(detailSchemeId));

      if (!nextScheme) {
        return false;
      }

      const user = store.state.user;
      user.schemes.forEach((x) => {
        x.curr = String(x.id) === String(detailSchemeId);
      });

      store.commit("loadUser", user);
      return true;
    };

    const handleInlineClose = (payload = null) => {
      const switchedScheme = syncSchemeFromDetail(payload);

      selectedEncounter.value = null;
      if (route.query.encounterId) {
        router.replace({ name: "auth" });
      }
      if (switchedScheme) {
        isLoading.value = true;
      }
      scrollToPageTop();
    };

    const handleInlineSaved = () => {
      selectedEncounter.value = null;
      if (route.query.encounterId) {
        router.replace({ name: "auth" });
      }
      if (currUrl.value) {
        isLoading.value = true;
        getItemsFromApi(currUrl.value);
      }
      scrollToPageTop();
    };

    const refreshRealtimeList = (event, page = 1) => {
      if (selectedEncounter.value || !currUrl.value || hasActiveSearch.value) {
        pendingRealtimeListPage.value = null;
        return false;
      }

      if (isLoading.value) {
        pendingRealtimeListPage.value = page;
        return true;
      }

      pendingRealtimeListPage.value = null;
      isLoading.value = true;
      error.value = null;
      getItemsFromApi(
        `${currUrl.value}${currHook.value || "&"}page=${page}`,
        { background: true }
      );
      return true;
    };

    watch(isLoading, loading => {
      if (!loading && pendingRealtimeListPage.value !== null) {
        refreshRealtimeList(null, pendingRealtimeListPage.value);
      }
    });

    const refreshRealtimeDetail = () => {
      return authInlineReview.value?.refreshFromServer?.() || false;
    };

    const refreshRealtimeMessages = () => {
      return authInlineReview.value?.refreshMessagesFromServer?.() || false;
    };

    const switchRealtimeScheme = async (schemeId) => {
      const user = store.state.user;
      const schemes = Array.isArray(user?.schemes) ? user.schemes : [];
      const selectedScheme = schemes.find(scheme => authSchemeId(scheme) === authSchemeId(schemeId));

      if (!selectedScheme) return false;

      store.commit("loadUser", {
        ...user,
        schemes: schemes.map(scheme => ({
          ...scheme,
          curr: authSchemeId(scheme) === authSchemeId(schemeId),
        })),
      });

      selectedEncounter.value = null;
      if (route.query.encounterId) {
        await router.replace({ name: "auth" });
      }

      showSearchFilters.value = true;
      searchTitle.value = null;
      hasActiveSearch.value = false;
      filterResetKey.value += 1;
      currUrl.value = authListUrl(authSchemeId(schemeId));
      currHook.value = "&";
      isLoading.value = true;
      getItemsFromApi(currUrl.value);
      scrollToPageTop();
      return true;
    };

    const realtimeTarget = {
      context: () => ({
        encounterId: selectedEncounter.value?.id || route.query.encounterId || null,
        hasSearch: hasActiveSearch.value,
        schemeId: defaultAuthSchemeId(),
        currentPage: Number(allPages.value?.current_page || 1),
      }),
      refreshList: refreshRealtimeList,
      refreshDetail: refreshRealtimeDetail,
      refreshMessages: refreshRealtimeMessages,
      switchScheme: switchRealtimeScheme,
    };
    const unregisterRealtimeTarget = registerAuthorizationRealtimeTarget("auth", realtimeTarget);
    onUnmounted(unregisterRealtimeTarget);

    watch(
      () => route.query.encounterId,
      (encounterId) => {
        selectedEncounter.value = encounterId ? { id: encounterId } : null;

        if (encounterId) {
          scrollToPageTop();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      if (store.state.realtimeAuthSchemeSwitch) {
        showSearchFilters.value = true;
        store.commit("queueRealtimeAuthSchemeSwitch", null);
      }

      if (!currUrl.value) {
        const schemeId = defaultAuthSchemeId();
        currUrl.value = authListUrl(schemeId);
        currHook.value = "&";
        getItemsFromApi(currUrl.value);
      }
    });

    // output results
    const encounters = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      handleActive,
      toggleSearchFilters,
      hasPendingAuth,
      home,
      currTab,
      tabs,
      showSearchFilters,
      selectedEncounter,
      ageSex,
      showAgeSex,
      numberBadge,
      timeSince,
      handlePage,
      handleSearch,
      handleEdit,
      handleMessages,
      encounterMessageCount,
      encounterUnreadMessageCount,
      encounterMessageLabel,
      handleInlineClose,
      handleInlineSaved,
      isPostAddDetail,
      initialDetailState,
      isReady,
      isLoading,
      isEmpty,
      error,
      searchTitle,
      encounters,
      allPages,
      filterResetKey,
      authInlineReview,
    };
  },
};
</script>

<style scoped>
.auth-breadcrumb {
  background: #fafafa !important;
  position: relative;
}

.auth-breadcrumb::after {
  background: #c4d3d9;
  bottom: 0;
  content: "";
  height: 1px;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
}

.auth-page-shell {
  margin-top: -14px;
  padding-top: 0;
  padding-bottom: 18px;
  position: relative;
  z-index: 1;
}

.auth-page-shell.has-detail {
  padding-bottom: 88px;
}

.auth-workbench {
  color: #263238;
}

.auth-review-head,
.auth-filter-shell,
.auth-search-summary,
.auth-line-loader,
.auth-list-card,
.auth-row-card,
.auth-empty-state,
.auth-detail-return-strip,
.auth-workbench :deep(.auth-inline-detail),
.auth-workbench :deep(.auth-inline-leader),
.auth-workbench :deep(.auth-inline-switch),
.auth-workbench :deep(.auth-created-guide),
.auth-workbench :deep(.auth-inline-review-card),
.auth-workbench :deep(.auth-inline-secondary-card) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.auth-review-head,
.auth-filter-shell,
.auth-search-summary,
.auth-list-card,
.auth-row-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-sizing: border-box;
  box-shadow: none;
  width: 100%;
}

.auth-review-head {
  align-items: flex-end;
  background: #ffffff;
  border-color: #c4d3d9;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: flex;
  gap: 22px;
  justify-content: space-between;
  margin-bottom: 0;
  overflow: visible;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.auth-review-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.auth-review-head.has-inline-detail {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: none;
}

.auth-detail-return-strip {
  align-items: center;
  background: #fff8df;
  border: 1px solid #f7c84b;
  border-top-color: #c4d3d9;
  border-radius: 6px 6px 0 0;
  box-shadow: none;
  box-sizing: border-box;
  color: #263238;
  display: flex;
  gap: 12px;
  min-height: 52px;
  padding: 10px 18px;
  width: 100%;
}

.auth-detail-return-button {
  align-items: center;
  background: #00897b;
  border: 1px solid #00796b;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 34px;
  justify-content: center;
  padding: 0;
  transition: background 160ms ease, transform 160ms ease;
  width: 34px;
}

.auth-detail-return-button:hover {
  background: #00695c;
  transform: translateY(-1px);
}

.auth-detail-return-button i {
  font-size: 20px;
}

.auth-detail-return-strip strong {
  display: block;
  font-size: 0.86rem;
  line-height: 1.15;
  text-transform: uppercase;
}

.auth-detail-return-strip span {
  color: #5f6f73;
  display: block;
  font-size: 0.84rem;
  line-height: 1.25;
  margin-top: 2px;
}

.auth-title-row {
  align-items: flex-end;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  position: relative;
  width: 100%;
  z-index: 2;
}

.auth-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.auth-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 780px;
}

.auth-search-toggle {
  align-items: center;
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 999px;
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 8px;
  min-height: 40px;
  padding: 0 13px 0 16px;
}

.auth-search-toggle i {
  color: #00897b;
  font-size: 19px;
}

.auth-filter-shell {
  border-color: #c4d3d9;
  border-radius: 0;
  border-top: 0;
  box-shadow: 0 14px 24px -24px rgba(38, 50, 56, 0.68);
  padding: 18px 24px 22px;
}

.auth-filter-card {
  background: #ffffff;
  border: 1px solid #c7d3d8;
  border-radius: 6px;
  box-shadow: none;
  overflow: visible;
  padding: 10px;
  width: 100%;
}

.auth-filter-card :deep(.sectionx > br) {
  display: none;
}

.auth-filter-card :deep(form),
.auth-filter-card :deep(.row) {
  margin: 0;
}

.auth-filter-card :deep(.row) {
  align-items: stretch;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 10px;
}

.auth-filter-card :deep(.col) {
  border: 1px solid #c7d3d8;
  border-radius: 6px;
  min-width: 0;
  padding: 0 !important;
  width: 100% !important;
}

.auth-filter-card :deep(nav) {
  background: transparent !important;
  box-shadow: none !important;
  height: auto;
  line-height: normal;
  min-height: 52px;
}

.auth-filter-card :deep(.input-field) {
  height: auto;
  margin-bottom: 0;
  margin-top: 0;
  min-height: 52px;
}

.auth-filter-card :deep(.nav-wrapper) {
  background: transparent;
  height: auto;
  line-height: normal;
  min-height: 52px;
}

.auth-filter-card :deep(input),
.auth-filter-card :deep(.select-wrapper input.select-dropdown) {
  background: transparent;
  border: none !important;
  box-sizing: border-box;
  color: #263238 !important;
  font-size: 1.02rem;
  font-weight: 700;
  height: 52px !important;
  line-height: 52px;
  margin: 0;
  padding: 0 42px 0 16px !important;
  width: 100%;
}

.auth-filter-card :deep(input:focus) {
  box-shadow: inset 0 -2px 0 #00897b !important;
}

.auth-filter-card :deep(.material-icons) {
  color: #00897b !important;
  height: 52px;
  line-height: 52px;
  right: 10px;
  top: 0;
}

.auth-filter-card :deep(.dropdown-content) {
  border-top: 1px solid #dfe7ea;
  box-shadow: none;
  margin: 0;
  max-height: 190px;
  overflow-y: auto;
  position: static !important;
  transform: none !important;
  width: 100% !important;
  z-index: 1;
}

.auth-filter-card :deep(.dropdown-content li > span) {
  color: #00897b;
  line-height: 1.35;
  padding: 12px 20px;
}

.auth-search-summary {
  align-items: center;
  background: #f7f7f7;
  border-color: #c4d3d9;
  border-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: 0 12px 22px -24px rgba(38, 50, 56, 0.68);
  display: flex;
  gap: 14px;
  justify-content: space-between;
  padding: 14px 24px;
}

.summary-label {
  color: #607d8b;
  flex: 0 0 auto;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.summary-content {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  min-width: 0;
  text-align: right;
}

.auth-search-summary :deep(.chip),
.summary-chip {
  background: #ffffff !important;
  border: 1px solid #e7c967;
  border-radius: 999px;
  color: #263238 !important;
  font-size: 0.82rem;
  font-weight: 700;
  height: auto;
  line-height: 1.2;
  margin: 0 6px 6px 0;
  padding: 7px 12px;
}

.auth-search-summary :deep(.chip:first-child),
.summary-chip {
  border-color: #80cbc4;
}

.auth-list-card {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
  display: grid;
  gap: 12px;
  margin-top: 16px;
  overflow: visible;
  padding: 14px;
}

.auth-row-card {
  background: linear-gradient(180deg, #ffffff 0%, #fdfdfd 100%);
  border: 1px solid #dfe7ea;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  padding: 27px 26px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, background-color 0.18s ease;
}

.auth-row-card:hover {
  background: linear-gradient(180deg, #ffffff 0%, #f8fbfc 100%);
  border-color: #b7cbd2;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95), 0 20px 36px -28px rgba(38, 50, 56, 0.82);
  transform: translateY(-1px);
}

.auth-row-card.needs-review {
  border-color: #ffcdd2;
}

.auth-row-card.needs-review:hover {
  border-color: #ef9a9a;
}

.auth-row-grid {
  align-items: center;
  display: grid;
  gap: 22px;
  grid-template-columns: 52px minmax(0, 1.65fr) minmax(0, 0.95fr) minmax(0, 1.2fr) minmax(0, 0.65fr) 72px;
}

.auth-number {
  align-items: center;
  background: #f4f7f8;
  border: 1px solid #e1e8eb;
  border-radius: 50%;
  color: #455a64;
  display: inline-flex;
  font-weight: 700;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.auth-person h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.35;
  margin: 0;
  overflow-wrap: anywhere;
}

.auth-meta-line,
.auth-contact-line {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.auth-meta-line {
  margin-top: 8px;
}

.auth-contact-line {
  color: #607d8b;
  font-size: 0.92rem;
  font-weight: 600;
  gap: 8px;
  line-height: 1.35;
  margin-top: 8px;
}

.auth-contact-line strong {
  color: #263238;
  font-weight: 700;
}

.contact-dot {
  background: #cfd8dc;
  border-radius: 50%;
  display: inline-block;
  height: 5px;
  width: 5px;
}

.auth-client {
  color: #607d8b;
  font-size: 0.88rem;
  font-weight: 600;
  margin: 7px 0 0;
}

.auth-type-chip,
.auth-soft-chip,
.auth-status-chip {
  border: 1px solid #dfe6e9;
  border-radius: 6px;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 7px 10px;
}

.auth-type-chip {
  background: #f2f5f6;
  color: #455a64;
}

.auth-type-chip.is-principal {
  background: #e8f3f2;
  border-color: #80cbc4;
  color: #00695c;
}

.auth-soft-chip {
  background: #f7f7f7;
  border-color: #eceff1;
  color: #455a64;
}

.auth-soft-chip.is-warning {
  background: #fbf6e6;
  border-color: #e7c967;
  color: #6d5412;
}

.auth-status-chip {
  background: #eef5fb;
  border-color: #d7e6f1;
  color: #285a82;
}

.auth-status-chip.is-pending {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.auth-data-cell {
  min-width: 0;
}

.auth-data-cell > span:first-child {
  color: #78909c;
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.auth-data-cell p {
  color: #263238;
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.35;
  margin: 0;
  overflow-wrap: anywhere;
}

.auth-action-cell {
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auth-message-action {
  align-items: center;
  background: #f2f8f8;
  border: 1px solid #c9dfe0;
  border-radius: 6px;
  color: #00695c;
  cursor: pointer;
  display: inline-flex;
  gap: 5px;
  height: 34px;
  justify-content: center;
  padding: 0 8px;
  position: relative;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  width: 100%;
}

.auth-message-action.has-unread {
  border-color: #80cbc4;
  box-shadow: 0 0 0 2px rgba(0, 137, 123, 0.08);
}

.auth-message-unread {
  align-items: center;
  background: #f9a825;
  border: 2px solid #ffffff;
  border-radius: 10px;
  color: #ffffff;
  display: inline-flex;
  font-size: 0.64rem;
  font-weight: 800;
  height: 20px;
  justify-content: center;
  line-height: 1;
  min-width: 20px;
  padding: 0 4px;
  position: absolute;
  right: -7px;
  top: -8px;
}

.auth-message-action:hover,
.auth-message-action:focus-visible {
  background: #e0f2f1;
  border-color: #80cbc4;
  color: #004d40;
  outline: 0;
}

.auth-message-action i {
  font-size: 17px;
}

.auth-message-action span {
  font-size: 0.82rem;
  font-weight: 800;
}

.auth-message-action small {
  display: none;
}

.auth-row-action {
  align-items: center;
  background: #00838f;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  padding: 0;
  width: 100%;
}

.auth-row-action.is-review {
  background: #c62828;
}

.auth-row-action i {
  font-size: 20px;
}

.auth-empty-state {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  color: #607d8b;
  margin-top: 16px;
  padding: 34px 22px;
  text-align: center;
}

.auth-empty-state p {
  margin: 8px 0 0;
}

.auth-line-loader {
  border-radius: 0;
  height: 3px;
  margin: 0 0 14px;
}

.custom-hr {
  width: 50%;
  border: none;
  border-top: 2px solid #ccc;
  margin: 20px 20px;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

.cancel-item:hover .material-icons.grey-text,
.cancel-item:hover .material-icons.grey-text::before {
  color: rgb(214, 16, 16) !important;
}

.clickable {
  cursor: pointer;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.clickable:hover {
  opacity: 0.8;
}

.pointable {
  cursor: default;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.pointable:hover {
  opacity: 0.8;
}

.auth-home-crumb {
  cursor: pointer;
}

@media only screen and (max-width: 992px) {
  .auth-page-shell.has-detail {
    max-width: none;
    padding-bottom: 12px;
    width: 96%;
  }

  .auth-page-shell.has-detail .auth-detail-return-strip {
    border-radius: 6px;
    margin-bottom: 8px;
  }

  .auth-title-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .auth-row-grid {
    gap: 18px;
    grid-template-columns: minmax(0, 1.5fr) minmax(180px, 0.9fr) minmax(180px, 1fr) minmax(120px, 0.7fr) 72px;
  }
}

@media only screen and (max-width: 600px) {
  .auth-page-shell.has-detail {
    padding-bottom: 10px;
    width: calc(100% - 16px);
  }

  .auth-page-shell.has-detail .auth-detail-return-strip {
    padding: 9px 12px;
  }

  .auth-review-head {
    padding: 22px;
  }

  .auth-title-block h5 {
    font-size: 1.45rem;
  }

  .auth-title-block p {
    font-size: 0.94rem;
  }

  .auth-search-toggle {
    justify-content: space-between;
    width: 100%;
  }

  .auth-filter-card {
    padding: 10px;
  }

  .auth-search-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
  }

  .summary-content {
    justify-content: flex-start;
    text-align: left;
  }

  .auth-list-card {
    background: transparent;
    border: 0;
    gap: 11px;
    padding: 0;
  }

  .auth-row-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .auth-row-card {
    border-radius: 8px;
    padding: 16px;
  }

  .auth-person h6 {
    font-size: 1rem;
  }

  .auth-data-cell {
    border-top: 1px solid #edf2f4;
    padding-top: 12px;
  }

  .auth-data-cell p {
    font-size: 0.92rem;
  }

  .auth-action-cell {
    border-top: 1px solid #edf2f4;
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-top: 12px;
  }

  .auth-message-action {
    height: 42px;
  }

  .auth-message-action small {
    display: inline;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .auth-row-action {
    border-radius: 8px;
    height: 42px;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-review-head,
  .auth-filter-shell,
  .auth-search-summary,
  .auth-line-loader,
  .auth-list-card,
  .auth-row-card,
  .auth-empty-state,
  .auth-workbench :deep(.auth-inline-detail),
  .auth-workbench :deep(.auth-inline-leader),
  .auth-workbench :deep(.auth-inline-switch),
  .auth-workbench :deep(.auth-created-guide),
  .auth-workbench :deep(.auth-inline-review-card),
  .auth-workbench :deep(.auth-inline-secondary-card) {
    animation: none;
  }
}
</style>
